import React, { useState, useContext } from 'react';
import classnames from 'classnames';
import GlobalProps from '../globalProps';
import { Card } from '../Card/Card';
import { Form } from '../Form/Form';
import { Select } from '../Form/Select/Select';
import { TextField } from '../Form/TextField/TextField';
import { Button } from '../Button/Button';
import { handleError } from '../../model/notification';
import { t } from '../../locales/translate';
import './Seatmap.css';
import { allSeatsEditable, EditableSeatStatusType } from '../../model/contingent';
import { ActivitiesContext } from '../Activities/Activities';
import { Checkbox } from '../Form/Checkbox/Checkbox';
import { SubmitLine } from '../SubmitLine/SubmitLine';
import { GroupedSeats } from './GroupedSeats';
import { Stack } from '../Stack/Stack';
import { Spacer } from '../Spacer/Spacer';
import { Seat } from '../../model/seatmap/types';
import { Seatmap } from '../../model/seatmap/seatmap';

interface BlockStatus {
  status: EditableSeatStatusType;
  permanent?: boolean;
  blockReason?: string;
}

interface Props extends GlobalProps {
  selectedSeats: Seat[];
  onDeselectAll: () => void;
  seatmap: Seatmap;
}

export function SeatmapEditBox({ className, style, selectedSeats, onDeselectAll, seatmap }: Props): JSX.Element {
  const blockStatus = selectedSeats[0].allocation?.blockReason ? 'blocked' : 'available';
  const [model, setModel] = useState<BlockStatus>({
    status: blockStatus,
    blockReason: selectedSeats[0].allocation?.blockReason,
    permanent: selectedSeats[0].allocation?.permanent,
  });
  const [loading, setLoading] = useState(false);
  const { reloadActivities } = useContext(ActivitiesContext);

  function sameSelection(): boolean {
    return (
      selectedSeats.every((seat) => seat.allocation?.blockReason) ||
      selectedSeats.every((seat) => !seat.allocation?.blockReason)
    );
  }

  function isModelChanged(): boolean {
    return Object.keys(model).some((key) => {
      if (key === 'status') {
        return model[key] !== blockStatus;
      }
      return model[key] !== selectedSeats[0][key];
    });
  }

  const canSubmit =
    // Check if loading
    !loading &&
    // If the selection is all the same we check for changes
    (sameSelection() ? isModelChanged() : true) &&
    // If blocked, block reason must exist
    (model.status === 'blocked' ? model.blockReason : true);

  if (!allSeatsEditable(selectedSeats)) return <></>;

  async function handleOnSubmit(): Promise<void> {
    setLoading(true);
    try {
      if (model.status === 'blocked') {
        await seatmap.blockSeats({
          seats: selectedSeats,
          reason: model.blockReason ?? '',
          permanent: !!model.permanent,
        });
      } else {
        await seatmap.unblockSeats(selectedSeats);
      }
      onDeselectAll();
      reloadActivities();
    } catch (error) {
      handleError(error);
    }
    setLoading(false);
  }

  return (
    <Form model={model} onInput={(v) => setModel(v)}>
      <Card
        title={t.model.venueLayout.selected}
        className={classnames('seatmap__edit-box', className)}
        style={style}
        actions={`(${selectedSeats.length})`}
        size="xs"
      >
        <Stack size="xs">
          <GroupedSeats seats={selectedSeats} />
          <Select
            label={t.model.status._name}
            name="status"
            required
            options={[
              { value: 'blocked', label: t.model.status.blocked },
              { value: 'available', label: t.model.status.available },
            ]}
          />
          {model.status === 'blocked' && (
            <>
              <Spacer size="none" />
              <Checkbox name="permanent" label={t.model.status.permanent} />
              <TextField name="blockReason" label={t.model.contingent.blockReason} required />
            </>
          )}
        </Stack>
        <Spacer size="sm" />
        <SubmitLine>
          <Button title={t.actions.cancel('')} onClick={onDeselectAll} size="overlay" />
          <Button
            title={t.actions.save('')}
            type="primary"
            loading={loading}
            disabled={!canSubmit}
            onClick={() => handleOnSubmit()}
            size="overlay"
          />
        </SubmitLine>
      </Card>
    </Form>
  );
}
